import React from "react";

interface FileUpload {
  id: string;
  filePath: string[];
  status: string;
  attachmentId: string;
  fileName: string;
}

interface LogDetailsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  log: {
    id: string;
    actionType: string;
    timeStamp: string;
    userId: string;
    username?: string;
    entityType: string;
    newValue: Record<string, any>;
    previousValue: Record<string, any>;
  } | null;
}

interface JobTradeAssociation {
  id: string;
  job_id: string;
  tradeType: {
    id: string;
    category: string;
    name: string;
  };
  unionStatus: string;
  isSignatory: boolean;
  numOfWorkers: number;
  localUnionIds: {
    id: string;
    code: string;
    unionNumber: number;
  }[];
  tradeContractAssociations: {
    id: string;
    noOfJourneymen: number;
    noOfApperntices: number;
    contractor: {
      id: string;
      name: string;
      email: string;
    };
    tradeType: {
      id: string;
      category: string;
      name: string;
    };
    projectStartDate: string;
  }[];
}

const compareJobTradeAssociations = (
  prevAssociations: any[] = [],
  newAssociations: any[] = [],
  showPrevious = true,
  jobTradePrev: any = {},
  jobTradeNew: any = {}
) => {
  const output: string[] = [];

  const prevTrades = jobTradePrev?.noOfTrades ?? "N/A";
  const newTrades = jobTradeNew?.noOfTrades ?? "N/A";
  if (prevTrades !== newTrades) {
    output.push(`No. of Trades: ${showPrevious ? prevTrades : newTrades}`);
    output.push("");
  }

  const matchedIds = new Set();

  prevAssociations.forEach((prevAssoc: any) => {
    const newAssoc = newAssociations.find((n) => n.id === prevAssoc.id);

    const tradeTypeNamePrev =
      prevAssoc.tradeType?.name ??
      prevAssoc.tradeContractAssociations?.[0]?.tradeType?.name ??
      "N/A";
    const tradeTypeNameNew =
      newAssoc?.tradeType?.name ??
      newAssoc?.tradeContractAssociations?.[0]?.tradeType?.name ??
      "N/A";

    const assocChanges: string[] = [];

    // Avoid showing "Trade Type: N/A" in new value if association was deleted
    if (newAssoc || showPrevious) {
      const tradeLabel = showPrevious ? tradeTypeNamePrev : tradeTypeNameNew;
      assocChanges.push(`Trade Type: ${tradeLabel}`);
    }

    // 🟥 Entire association deleted (not present in new) – in New Value
    if (!newAssoc) {
      if (!showPrevious) {
        assocChanges.push(`Trade Type: ${tradeTypeNamePrev} (Deleted)`);
        assocChanges.push(`This trade association was removed.`);
        output.push(assocChanges.join("\n"));
        output.push("");
      } else {
        // full details in Previous Value
        const unionStatus = prevAssoc.unionStatus ?? "N/A";
        const isSignatory = prevAssoc.isSignatory ? "Yes" : "No";
        const numOfWorkers = prevAssoc.numOfWorkers ?? "N/A";
        assocChanges.push(`Union Status: ${unionStatus}`);
        assocChanges.push(`Is Signatory: ${isSignatory}`);
        assocChanges.push(`No. of Workers: ${numOfWorkers}`);

        if (prevAssoc.localUnionIds?.length) {
          const unions = prevAssoc.localUnionIds.map(
            (u: any) => `${u.code} - ${u.unionNumber}`
          );
          assocChanges.push(`Local Unions: ${unions.join(", ")}`);
        }

        prevAssoc.tradeContractAssociations?.forEach((contract: any) => {
          assocChanges.push(
            `Contractor: ${contract.contractor?.name ?? "N/A"}`,
            `Journeymen: ${contract.noOfJourneymen ?? "N/A"}`,
            `Apprentices: ${contract.noOfApperntices ?? "N/A"}`
          );
        });

        output.push(assocChanges.join("\n"));
        output.push("");
      }
      return;
    }

    matchedIds.add(prevAssoc.id);

    if (prevAssoc.unionStatus !== newAssoc.unionStatus) {
      assocChanges.push(
        `Union Status: ${
          showPrevious ? prevAssoc.unionStatus : newAssoc.unionStatus
        }`
      );
    }

    if (prevAssoc.isSignatory !== newAssoc.isSignatory) {
      let isSignatoryLabel: string;
      if (showPrevious) {
        if (prevAssoc.isSignatory === true) {
          isSignatoryLabel = "Yes";
        } else {
          isSignatoryLabel = "No";
        }
      } else {
        if (newAssoc.isSignatory === true) {
          isSignatoryLabel = "Yes";
        } else {
          isSignatoryLabel = "No";
        }
      }
      assocChanges.push(`Is Signatory: ${isSignatoryLabel}`);
    }

    if (prevAssoc.numOfWorkers !== newAssoc.numOfWorkers) {
      assocChanges.push(
        `No. of Workers: ${
          showPrevious ? prevAssoc.numOfWorkers : newAssoc.numOfWorkers
        }`
      );
    }

    const prevUnions =
      prevAssoc.localUnionIds
        ?.map((u: any) => `${u.code} - ${u.unionNumber}`)
        .join(", ") ?? "";
    const newUnions =
      newAssoc.localUnionIds
        ?.map((u: any) => `${u.code} - ${u.unionNumber}`)
        .join(", ") ?? "";
    if (prevUnions !== newUnions) {
      assocChanges.push(
        `Local Unions: ${showPrevious ? prevUnions : newUnions}`
      );
    }

    const prevContracts = prevAssoc.tradeContractAssociations || [];
    const newContracts = newAssoc.tradeContractAssociations || [];
    const matchedContractIds = new Set();

    // 🔄 Compare matched contracts
    prevContracts.forEach((prevContract: any) => {
      const newContract = newContracts.find(
        (c: any) => c.id === prevContract.id
      );

      if (!newContract) {
        if (!showPrevious) {
          assocChanges.push(
            `Contractor: ${
              prevContract.contractor?.name ?? "Unknown"
            } (Deleted)`,
            `Journeymen: -`,
            `Apprentices: -`
          );
        } else {
          assocChanges.push(
            `Contractor: ${prevContract.contractor?.name ?? "N/A"}`,
            `Journeymen: ${prevContract.noOfJourneymen ?? "N/A"}`,
            `Apprentices: ${prevContract.noOfApperntices ?? "N/A"}`
          );
        }
        return;
      }

      matchedContractIds.add(prevContract.id);

      if (prevContract.contractor?.name !== newContract.contractor?.name) {
        assocChanges.push(
          `Contractor: ${
            showPrevious
              ? prevContract.contractor?.name
              : newContract.contractor?.name
          }`
        );
      }

      if (prevContract.noOfJourneymen !== newContract.noOfJourneymen) {
        assocChanges.push(
          `Journeymen: ${
            showPrevious
              ? prevContract.noOfJourneymen
              : newContract.noOfJourneymen
          }`
        );
      }

      if (prevContract.noOfApperntices !== newContract.noOfApperntices) {
        assocChanges.push(
          `Apprentices: ${
            showPrevious
              ? prevContract.noOfApperntices
              : newContract.noOfApperntices
          }`
        );
      }
    });

    // ➕ New Contracts (only in newValue)
    if (!showPrevious) {
      newContracts.forEach((contract: any) => {
        if (matchedContractIds.has(contract.id)) return;
        assocChanges.push(
          `Contractor: ${contract.contractor?.name ?? "N/A"}`,
          `Journeymen: ${contract.noOfJourneymen ?? "N/A"}`,
          `Apprentices: ${contract.noOfApperntices ?? "N/A"}`
        );
      });
    }

    if (assocChanges.length > 1) {
      output.push(assocChanges.join("\n"));
      output.push("");
    }
  });

  // ➕ Entirely new trade associations
  if (!showPrevious) {
    newAssociations.forEach((assoc) => {
      if (matchedIds.has(assoc.id)) return;

      const tradeTypeName = assoc.tradeType?.name ?? "N/A";
      const unionStatus = assoc.unionStatus ?? "N/A";
      const isSignatory = assoc.isSignatory ? "Yes" : "No";
      const numOfWorkers = assoc.numOfWorkers ?? "N/A";

      output.push(
        `Trade Type: ${tradeTypeName}`,
        `Union Status: ${unionStatus}`,
        `Is Signatory: ${isSignatory}`,
        `No. of Workers: ${numOfWorkers}`
      );

      if (assoc.localUnionIds?.length) {
        const unions = assoc.localUnionIds.map(
          (u: any) => `${u.code} - ${u.unionNumber}`
        );
        output.push(`Local Unions: ${unions.join(", ")}`);
      }

      assoc.tradeContractAssociations?.forEach((contract: any) => {
        output.push(
          `Contractor: ${contract.contractor?.name ?? "N/A"}`,
          `Journeymen: ${contract.noOfJourneymen ?? "N/A"}`,
          `Apprentices: ${contract.noOfApperntices ?? "N/A"}`
        );
      });

      output.push("");
    });
  }

  return output.join("\n");
};

/**
 * Compare Jobsite Visit Trade Associations for UPDATE logs
 * Similar to compareJobTradeAssociations but for visit trades
 */
const compareVisitTradeAssociations = (
  prevAssociations: any[] = [],
  newAssociations: any[] = [],
  showPrevious = true,
  jobTradePrev: any = {},
  jobTradeNew: any = {},
  prevTotalWorkers?: number,
  newTotalWorkers?: number
) => {
  const output: string[] = [];

  // Use actual array length for accurate count
  const prevTrades = prevAssociations?.length ?? "N/A";
  const newTrades = newAssociations?.length ?? "N/A";
  if (prevTrades !== newTrades) {
    output.push(`No. of Trades: ${showPrevious ? prevTrades : newTrades}`);
  }

  // Show total workers if changed
  if (prevTotalWorkers !== undefined && newTotalWorkers !== undefined && prevTotalWorkers !== newTotalWorkers) {
    output.push(`Total Workers: ${showPrevious ? prevTotalWorkers : newTotalWorkers}`);
  }

  if (output.length > 0) {
    output.push("");
  }

  const matchedIds = new Set();

  prevAssociations.forEach((prevAssoc: any) => {
    const newAssoc = newAssociations.find((n) => n.id === prevAssoc.id);

    const tradeTypeNamePrev = prevAssoc.tradeType?.name ?? "N/A";
    const tradeTypeNameNew = newAssoc?.tradeType?.name ?? "N/A";

    const assocChanges: string[] = [];

    if (newAssoc || showPrevious) {
      const tradeLabel = showPrevious ? tradeTypeNamePrev : tradeTypeNameNew;
      assocChanges.push(`Trade Type: ${tradeLabel}`);
    }

    // Association deleted
    if (!newAssoc) {
      if (!showPrevious) {
        assocChanges.push(`Trade Type: ${tradeTypeNamePrev} (Deleted)`);
        assocChanges.push(`This trade association was removed.`);
        output.push(assocChanges.join("\n"));
        output.push("");
      } else {
        const isUACanadaTrade = prevAssoc.isUACanadaTrade ? "Yes" : "No";
        if (prevAssoc.isUACanadaTrade) {
          assocChanges.push(`UA Canada Trade: ${isUACanadaTrade}`);
        }

        prevAssoc.tradeContractAssociations?.forEach((contract: any) => {
          const unionType = contract.unionType === 'union' ? 'Union' : 'Non-Union';
          assocChanges.push(
            `Contractor: ${contract.contractor?.name ?? "N/A"}`,
            `Union Type: ${unionType}`,
            `Local Union: ${contract.localUnion?.unionNumber ?? "N/A"}`,
            `Journeymen: ${contract.noOfJourneymen ?? "N/A"}`,
            `Apprentices: ${contract.noOfApperntices ?? "N/A"}`
          );
        });

        output.push(assocChanges.join("\n"));
        output.push("");
      }
      return;
    }

    matchedIds.add(prevAssoc.id);

    if (prevAssoc.isUACanadaTrade !== newAssoc.isUACanadaTrade) {
      const isUACanadaLabel = showPrevious
        ? prevAssoc.isUACanadaTrade ? "Yes" : "No"
        : newAssoc.isUACanadaTrade ? "Yes" : "No";
      assocChanges.push(`UA Canada Trade: ${isUACanadaLabel}`);
    }

    const prevContracts = prevAssoc.tradeContractAssociations || [];
    const newContracts = newAssoc.tradeContractAssociations || [];
    const matchedContractIds = new Set();

    // Compare matched contracts
    prevContracts.forEach((prevContract: any) => {
      const newContract = newContracts.find((c: any) => c.id === prevContract.id);

      if (!newContract) {
        if (!showPrevious) {
          assocChanges.push(
            `Contractor: ${prevContract.contractor?.name ?? "Unknown"} (Deleted)`,
            `Journeymen: -`,
            `Apprentices: -`
          );
        } else {
          const unionType = prevContract.unionType === 'union' ? 'Union' : 'Non-Union';
          assocChanges.push(
            `Contractor: ${prevContract.contractor?.name ?? "N/A"}`,
            `Union Type: ${unionType}`,
            `Local Union: ${prevContract.localUnion?.unionNumber ?? "N/A"}`,
            `Journeymen: ${prevContract.noOfJourneymen ?? "N/A"}`,
            `Apprentices: ${prevContract.noOfApperntices ?? "N/A"}`
          );
        }
        return;
      }

      matchedContractIds.add(prevContract.id);

      if (prevContract.contractor?.name !== newContract.contractor?.name) {
        assocChanges.push(
          `Contractor: ${showPrevious ? prevContract.contractor?.name : newContract.contractor?.name}`
        );
      }

      if (prevContract.unionType !== newContract.unionType) {
        const unionType = showPrevious
          ? (prevContract.unionType === 'union' ? 'Union' : 'Non-Union')
          : (newContract.unionType === 'union' ? 'Union' : 'Non-Union');
        assocChanges.push(`Union Type: ${unionType}`);
      }

      if (prevContract.localUnion?.unionNumber !== newContract.localUnion?.unionNumber) {
        assocChanges.push(
          `Local Union: ${showPrevious ? prevContract.localUnion?.unionNumber : newContract.localUnion?.unionNumber}`
        );
      }

      if (prevContract.noOfJourneymen !== newContract.noOfJourneymen) {
        assocChanges.push(
          `Journeymen: ${showPrevious ? prevContract.noOfJourneymen : newContract.noOfJourneymen}`
        );
      }

      if (prevContract.noOfApperntices !== newContract.noOfApperntices) {
        assocChanges.push(
          `Apprentices: ${showPrevious ? prevContract.noOfApperntices : newContract.noOfApperntices}`
        );
      }
    });

    // New Contracts
    if (!showPrevious) {
      newContracts.forEach((contract: any) => {
        if (matchedContractIds.has(contract.id)) return;
        const unionType = contract.unionType === 'union' ? 'Union' : 'Non-Union';
        assocChanges.push(
          `Contractor: ${contract.contractor?.name ?? "N/A"}`,
          `Union Type: ${unionType}`,
          `Local Union: ${contract.localUnion?.unionNumber ?? "N/A"}`,
          `Journeymen: ${contract.noOfJourneymen ?? "N/A"}`,
          `Apprentices: ${contract.noOfApperntices ?? "N/A"}`
        );
      });
    }

    if (assocChanges.length > 1) {
      output.push(assocChanges.join("\n"));
      output.push("");
    }
  });

  // New trade associations
  if (!showPrevious) {
    newAssociations.forEach((assoc) => {
      if (matchedIds.has(assoc.id)) return;

      const tradeTypeName = assoc.tradeType?.name ?? "N/A";

      output.push(`Trade Type: ${tradeTypeName}`);

      if (assoc.isUACanadaTrade) {
        output.push(`UA Canada Trade: Yes`);
      }

      assoc.tradeContractAssociations?.forEach((contract: any) => {
        const unionType = contract.unionType === 'union' ? 'Union' : 'Non-Union';
        output.push(
          `Contractor: ${contract.contractor?.name ?? "N/A"}`,
          `Union Type: ${unionType}`,
          `Local Union: ${contract.localUnion?.unionNumber ?? "N/A"}`,
          `Journeymen: ${contract.noOfJourneymen ?? "N/A"}`,
          `Apprentices: ${contract.noOfApperntices ?? "N/A"}`
        );
      });

      output.push("");
    });
  }

  return output.join("\n");
};

const LogDetailsOverlay: React.FC<LogDetailsOverlayProps> = ({
  isOpen,
  onClose,
  log,
}) => {
  if (!log) return null;
  const formatValue = (
    value: Record<string, any>,
    isPreviousValue: boolean = false
  ): string => {
    try {
      if (log.actionType === "CREATE" && log.entityType === "Comments") {
        const fields = [{ label: "Comment", value: value.comment }];
        return fields
          .filter((field) => field.value && field.value !== "N/A")
          .map((field) => `${field.label}: ${field.value}`)
          .join("\n");
      }

      if (
        log.actionType === "UPDATE" &&
        log.entityType === "Job Status"
      ) {
        // Get previous and new values for comparison
        const prevValue = isPreviousValue ? log.previousValue : null;
        const newValue = !isPreviousValue ? log.newValue : null;

        // Use the appropriate value based on which side we're displaying
        const displayValue = isPreviousValue ? prevValue : newValue;

        const fields = [
          {
            label: "Status",
            value: displayValue?.status
              ? displayValue.status.charAt(0).toUpperCase() + displayValue.status.slice(1)
              : "-",
          },
          {
            label: "Completion Date",
            value: displayValue?.markAsCompAt
              ? new Date(displayValue.markAsCompAt).toLocaleString()
              : "-",
          },
          {
            label: "Restored At",
            value: displayValue?.restoredAt
              ? new Date(displayValue.restoredAt).toLocaleString()
              : "-",
          },
          {
            label: "Restored By",
            value: displayValue?.restoredBy?.name || displayValue?.restoredBy || "-",
          },
          {
            label: "Restore Reason",
            value: displayValue?.restoreReason || "-",
          },
        ];

        return fields
          .filter((field) => field.value && field.value !== "N/A" && field.value !== "-")
          .map((field) => `${field.label}: ${field.value}`)
          .join("\n");
      }

      // Handle Job Details CREATE action type
      if (
        (log.actionType === "CREATE" ||
          log.actionType === "UPDATE" ||
          log.actionType === "DELETE") &&
        (log.entityType === "Job Details" ||  log.entityType === "Jobsite Delete")
      ) {
        const prevValue = log.actionType === "UPDATE" && isPreviousValue ? log.previousValue : null;
        const newValue = log.actionType === "UPDATE" && !isPreviousValue ? log.newValue : null;
        // Helper to get display value with (Empty) handling
        const getDisplayValue = (val: any) => val === "" ? "(Empty)" : val;
        const fields = [
          {
            label: "Job Site Name",
            key: "jobSiteName",
            value: getDisplayValue(value.jobSiteName),
            prevValue: prevValue?.jobSiteName,
            newValue: newValue?.jobSiteName,
            rawValue: value.jobSiteName
          },
          {
            label: "Subsite",
            key: "subsite",
            value: getDisplayValue(value.subsite),
            prevValue: prevValue?.subsite,
            newValue: newValue?.subsite,
            rawValue: value.subsite
          },
          {
            label: "Site Contact",
            key: "siteContact",
            value: getDisplayValue(value.siteContact),
            prevValue: prevValue?.siteContact,
            newValue: newValue?.siteContact,
            rawValue: value.siteContact
          },
          {
            label: "Site Contact Email",
            key: "siteContactEmail",
            value: getDisplayValue(value.siteContactEmail),
            prevValue: prevValue?.siteContactEmail,
            newValue: newValue?.siteContactEmail,
            rawValue: value.siteContactEmail
          },
          {
            label: "Status",
            key: "status",
            value: value.status
              ? value.status.charAt(0).toUpperCase() + value.status.slice(1)
              : '',
          },
          {
            label: "Mark as Complete",
            key: "markAsComp",
            value: value.markAsComp ? (value.markAsComp ? "Yes" : "No") : null,
          },
          {
            label: value.jobDetailsSectors && value.jobDetailsSectors.length > 1 ? "Sectors" : "Sector",
            key: "sectors",
            value: value.jobDetailsSectors?.map((jds: any) => jds.sector?.name).filter(Boolean).join(', ') || value.sector?.name,
            prevValue: prevValue?.jobDetailsSectors?.map((jds: any) => jds.sector?.id).filter(Boolean).sort().join(',') || prevValue?.sector?.id,
            newValue: newValue?.jobDetailsSectors?.map((jds: any) => jds.sector?.id).filter(Boolean).sort().join(',') || newValue?.sector?.id
          },
          {
            label: "Industry",
            key: "industry",
            value: value.industry?.name,
            compareValue: value.industry?.id,
            prevValue: prevValue?.industry?.name,
            newValue: newValue?.industry?.name
          },
          {
            label: "Funding Resource",
            key: "fundingResource",
            value: value.fundingResource?.name,
            compareValue: value.fundingResource?.id,
            prevValue: prevValue?.fundingResource?.name,
            newValue: newValue?.fundingResource?.name
          },
          {
            label: "Job Completion Status",
            key: "jobCompletionStatus",
            value: value.jobCompletionStatus?.status,
            compareValue: value.jobCompletionStatus?.id,
            prevValue: prevValue?.jobCompletionStatus?.status,
            newValue: newValue?.jobCompletionStatus?.status
          },
          {
            label: "General Contractor",
            key: "generalContractor",
            value: value.generalContractor?.name,
            compareValue: value.generalContractor?.id,
            prevValue: prevValue?.generalContractor?.name,
            newValue: newValue?.generalContractor?.name
          },
          {
            label: "Project Start Date",
            key: "projectStartDate",
            value: value.projectStartDate ? new Date(value.projectStartDate).toLocaleDateString() : '',
            compareValue: value.projectStartDate,
            prevValue: prevValue?.projectStartDate,
            newValue: newValue?.projectStartDate
          },
          { label: "Deleted At", key: "deletedAt", value: value.deletedAt ? new Date(value.deletedAt).toLocaleString(): value.deletedAt},
          { label: "Deletion Reason", key: "deletionReason", value: value.deletionReason },
          {
            label: "Deleted By",
            key: "deletedBy",
            value: value.deletedBy ? value.deletedBy?.name : "",
            compareValue: value.deletedBy?.id,
            prevValue: prevValue?.deletedBy?.name,
            newValue: newValue?.deletedBy?.name
          },
          {
            label: "PLA",
            key: "hasPLA",
            value: value.hasPLA ? "Yes" : "No",
            compareValue: value.hasPLA,
            prevValue: prevValue?.hasPLA,
            newValue: newValue?.hasPLA
          },
          {
            label: "MRF",
            key: "hasMRF",
            value: value.hasMRF ? "Yes" : "No",
            compareValue: value.hasMRF,
            prevValue: prevValue?.hasMRF,
            newValue: newValue?.hasMRF
          },
        ];

        // For UPDATE actions, only show fields that actually changed
        let filteredFields = fields;
        if (log.actionType === "UPDATE") {
          filteredFields = fields.filter(field => {
            if (!field.key) return true;
            // Special handling for sectors - compare directly from log values
            if (field.key === "sectors") {
              const prevSectors = log.previousValue?.jobDetailsSectors?.map((jds: any) => jds.sector?.id).filter(Boolean).sort().join(',') || log.previousValue?.sector?.id;
              const newSectors = log.newValue?.jobDetailsSectors?.map((jds: any) => jds.sector?.id).filter(Boolean).sort().join(',') || log.newValue?.sector?.id;
              return prevSectors !== newSectors;
            }

            // Compare using the actual log values for all fields
            // This ensures consistent comparison regardless of field type
            if (field.key) {
              const prevObj = log.previousValue?.[field.key];
              const newObj = log.newValue?.[field.key];
              return JSON.stringify(prevObj) !== JSON.stringify(newObj);
            }

            return false;
          });
        }

        // Use the appropriate value based on whether we're showing previous or new
        const displayValue = (field: any) => {
          // For fields with compareValue, fetch from log for both previous and new values
          if ('compareValue' in field && field.key) {
            const logValue = isPreviousValue ? log.previousValue?.[field.key] : log.newValue?.[field.key];
            if (logValue === null) return "N/A";
            if (logValue === undefined) return field.value;
            // Handle booleans
            if (typeof logValue === 'boolean') {
              return logValue ? "Yes" : "No";
            }
            // Handle dates
            if (field.key === 'projectStartDate' && logValue) {
              return new Date(logValue).toLocaleDateString();
            }
            // Extract the name property for display (for objects like contractor, industry)
            return getDisplayValue(logValue.name || logValue.status || logValue);
          }
          // For regular fields, use the pre-computed display value
          if (isPreviousValue && field.prevValue !== undefined) {
            return getDisplayValue(field.prevValue);
          }
          return field.value;
        };

        return filteredFields
          .filter((field) => {
            const val = displayValue(field);
            // For UPDATE actions, show N/A values (they represent removed fields)
            const shouldInclude = log.actionType === "UPDATE"
              ? (val !== undefined && val !== null && val !== "")
              : (val && val !== "N/A");
            return shouldInclude;
          })
          .map((field) => `${field.label}: ${displayValue(field)}`)
          .join("\n");
      }

      // Handle Job Address CREATE action type
      if (
        (log.actionType === "CREATE" || log.actionType === "UPDATE") &&
        log.entityType === "Job Address"
      ) {
        const fields = [
          { label: "Address Line 1", value: value.address1 },
          { label: "Address Line 2", value: value.address2 },
          { label: "City", value: value.city },
          { label: "Postal Code", value: value.postalCode },
          { label: "Province", value: value.province?.name },
          { label: "Board Area", value: value.boardArea?.name },
        ];

        return fields
          .filter((field) => field.value && field.value !== "N/A")
          .map((field) => `${field.label}: ${field.value}`)
          .join("\n");
      }

      // Handle Jobsite Visit Details
      if (
        (log.actionType === "CREATE" || log.actionType === "UPDATE") &&
        log.entityType === "Jobsite Visit Details"
      ) {
        const prevValue = log.actionType === "UPDATE" && isPreviousValue ? log.previousValue : null;
        const newValue = log.actionType === "UPDATE" && !isPreviousValue ? log.newValue : null;

        const fields = [
          {
            label: "Visit Date",
            key: "visitDate",
            value: value.visitDate
              ? new Date(value.visitDate).toLocaleDateString()
              : "(Empty)",
            prevValue: prevValue?.visitDate,
            newValue: newValue?.visitDate,
          },
          {
            label: "Status",
            key: "status",
            value: value.status
              ? value.status.charAt(0).toUpperCase() + value.status.slice(1)
              : "(Empty)",
            prevValue: prevValue?.status,
            newValue: newValue?.status,
          },
          {
            label: "Job Completion Status",
            key: "jobCompletionStatus",
            value: value.jobCompletionStatus || "(Empty)",
            prevValue: prevValue?.jobCompletionStatus,
            newValue: newValue?.jobCompletionStatus,
          },
        ];

        // For UPDATE actions, only show fields that actually changed
        let filteredFields = fields;
        if (log.actionType === "UPDATE") {
          filteredFields = fields.filter(field => {
            if (!field.key) return true;
            const prevVal = field.prevValue;
            const newVal = field.newValue;
            return JSON.stringify(prevVal) !== JSON.stringify(newVal);
          });
        }

        return filteredFields
          .filter((field) => field.value && field.value !== "N/A")
          .map((field) => `${field.label}: ${field.value}`)
          .join("\n");
      }

      // Handle Jobsite Visit Trades
      if (log.entityType === "Jobsite Visit Trades") {
        // Add visit date at the top if available
        const visitDatePrefix = value.visitDate
          ? `Visit Date: ${new Date(value.visitDate).toLocaleDateString()}\n\n`
          : '';

        if (log.actionType === "UPDATE") {
          return visitDatePrefix + compareVisitTradeAssociations(
            log.previousValue?.jobTradeAssociations || [],
            log.newValue?.jobTradeAssociations || [],
            isPreviousValue,
            log.previousValue?.jobTrade,
            log.newValue?.jobTrade,
            log.previousValue?.totalWorkers,
            log.newValue?.totalWorkers
          );
        }

        // For CREATE action, show all trade details
        if (log.actionType === "CREATE" && value.jobTradeAssociations) {
          const noOfTrades = value.jobTradeAssociations.length;
          const totalWorkers = value.totalWorkers || 0;
          const output: string[] = [
            visitDatePrefix.trim(), // Add visit date
            `No. of Trades: ${noOfTrades}`,
            `Total Workers: ${totalWorkers}`,
            ""
          ].filter(Boolean);

          value.jobTradeAssociations.forEach((assoc: any) => {
            const tradeName = assoc.tradeType?.name || "Unknown Trade";
            const uaCanada = assoc.isUACanadaTrade ? "UA Canada" : "";

            const tradeDetails: string[] = [
              `Trade Type: ${tradeName}`,
            ];

            if (uaCanada) {
              tradeDetails.push(`UA Canada Trade: Yes`);
            }

            // Add contractor details
            assoc.tradeContractAssociations?.forEach((contract: any) => {
              const unionType = contract.unionType === 'union' ? 'Union' : 'Non-Union';

              tradeDetails.push(
                `Contractor: ${contract.contractor?.name || "Unknown"}`,
                `Union Type: ${unionType}`,
                `Local Union: ${contract.localUnion?.unionNumber || "N/A"}`,
                `Journeymen: ${contract.noOfJourneymen || 0}`,
                `Apprentices: ${contract.noOfApperntices || 0}`
              );
            });

            output.push(tradeDetails.join("\n"));
            output.push("");
          });

          return output.join("\n").trim();
        }
      }

      // Handle Jobsite Visit Files
      if (
        (log.actionType === "CREATE" || log.actionType === "DELETE") &&
        log.entityType === "Jobsite Visit Files"
      ) {
        if (log.actionType === "DELETE") {
          const fields = [
            { label: "File Name", value: value.fileName || "(Unknown)" },
            { label: "Status", value: value.status || "(Unknown)" },
            {
              label: "File URL",
              value: Array.isArray(value.filePath)
                ? value.filePath[0]
                : value.filePath || "(Unknown)",
            },
          ];

          return fields
            .filter((field) => field.value && field.value !== "N/A")
            .map((field) => `${field.label}: ${field.value}`)
            .join("\n");
        }

        // For CREATE action
        if (value.uploadedFiles && Array.isArray(value.uploadedFiles)) {
          const fileDetails = value.uploadedFiles
            .map((file: any) => {
              const statusLabel = file.status === "present" ? "Active" : "Deleted";
              return [
                `File Name: ${file.fileName}`,
                `Status: ${statusLabel}`,
                `File URL: ${Array.isArray(file.filePath) ? file.filePath[0] : file.filePath}`,
              ].join("\n");
            })
            .join("\n\n");

          return fileDetails;
        }
      }

      // Handle Jobsite Visit Comments
      if (log.entityType === "Jobsite Visit Comments") {
        return `Comment: ${value.comment || "(Empty)"}`;
      }

      return "";
    } catch (e) {
      console.error("Error formatting value:", e);
      return "";
    }
  };
  const findChangedFields = (
    prev: Record<string, any>,
    next: Record<string, any>
  ): Record<string, any> => {
    // For all entity types
    const changedFields: Record<string, any> = {};
    Object.keys(next).forEach((key) => {
      if (["job_id", "updatedAt", "id"].includes(key)) return;

      const prevVal = prev[key];
      const newVal = next[key];

      if (JSON.stringify(prevVal) !== JSON.stringify(newVal)) {
        changedFields[key] = prevVal;
      }
    });

    return Object.keys(changedFields).length > 0 ? changedFields : {};
  };
  const getBadgeColor = (actionType: string): string => {
    const type = actionType.toLowerCase();
    if (type.includes("create") || type.includes("add")) {
      return "bg-green-100 text-green-800";
    } else if (type.includes("delete") || type.includes("remove")) {
      return "bg-red-100 text-red-800";
    } else if (type.includes("update") || type.includes("edit")) {
      return "bg-blue-100 text-blue-800";
    } else if (type.includes("submit")) {
      return "bg-purple-100 text-purple-800";
    } else {
      return "bg-gray-100 text-gray-800";
    }
  };

  const getUserActionLabel = (actionType: string): string => {
    switch (actionType) {
      case "CREATE":
        return "Created by";
      case "DELETE":
        return "Deleted by";
      default:
        return "Updated by";
    }
  };

  const getPreviousValueDisplay = (log: LogDetailsOverlayProps["log"]) => {
    if (!log) return "";
    if (log.actionType === "UPDATE" && log.entityType === "Jobsite Visit Details") {
      return formatValue(log.previousValue, true);
    }
    if (log.actionType === "UPDATE" && log.entityType === "Jobsite Visit Trades") {
      return formatValue(log.previousValue, true);
    }

    if (log.actionType === "UPDATE") {
      const changedFields = findChangedFields(log.previousValue, log.newValue);
      return formatValue(changedFields, true);
    }
    return formatValue(log.previousValue);
  };

  const getNewValueDisplay = (log: LogDetailsOverlayProps["log"]) => {
    if (!log) return "";
    if (log.actionType === "UPDATE" && log.entityType === "Jobsite Visit Details") {
      return formatValue(log.newValue, false);
    }
    if (log.actionType === "UPDATE" && log.entityType === "Jobsite Visit Trades") {
      return formatValue(log.newValue, false);
    }
    if (log.actionType === "UPDATE") {
      const changedKeys = Object.keys(
        findChangedFields(log.previousValue, log.newValue)
      );
      const filteredNewValue = Object.fromEntries(
        changedKeys.map((key) => [key, log.newValue?.[key]])
      );
      return formatValue(filteredNewValue, false);
    }
    return formatValue(log.newValue);
  };

  const getDeleteFileValueDisplay = (log: LogDetailsOverlayProps["log"]) => {
    if (!log) return "";
    return formatValue(log.newValue);
  };

  return (
    <div
      className={`fixed inset-0 bg-black transition-all duration-300 ease-in-out ${
        isOpen ? "bg-opacity-50" : "bg-opacity-0 pointer-events-none"
      } z-50`}
      onClick={e => {
        // Only close if the click is on the overlay itself, not the side panel
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      data-testid="log-details-overlay-bg"
    >
      <div
        className={`fixed right-0 bg-white h-screen  w-full md:w-[500px] shadow-lg transition-all duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Log Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto h-[calc(100vh-180px)]">
          <div className="space-y-4">
            {/* Action Type */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Action Type
              </label>
              <span
                className={`px-2 py-1 text-xs font-medium rounded-full ${getBadgeColor(
                  log.actionType
                )}`}
              >
                {log.actionType}
              </span>
            </div>

            {/* Entity Type */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Entity Type
              </label>
              <div className="text-sm">{log.entityType}</div>
            </div>

            {/* Timestamp */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Timestamp
              </label>
              <div className="text-sm">{log.timeStamp}</div>
            </div>

            {/* User */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                {getUserActionLabel(log.actionType)}
              </label>
              <div className="text-sm">{log.username || "Unknown User"}</div>
            </div>

            {/* Previous Value */}
            {Object.keys(log.previousValue).length > 0 &&
              log.actionType !== "DELETE" && (
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Previous Value
                  </label>
                  <div className="text-sm rounded-md whitespace-pre-line">
                    {getPreviousValueDisplay(log)}
                  </div>
                </div>
              )}

            {/* New Value */}
            {Object.keys(log.newValue).length > 0 &&
              log.actionType !== "DELETE" && (
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    New Value
                  </label>
                  <div className="text-sm rounded-md whitespace-pre-line">
                    {getNewValueDisplay(log)}
                  </div>
                </div>
              )}

            {/* Deleted File  Value */}
            {Object.keys(log.previousValue).length > 0 &&
              log.actionType === "DELETE" && (
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    {log.entityType === "Jobsite Delete"
                      ? "Deleted Job Site Details"
                      : "Deleted Entity Details"}
                  </label>
                  <div className="text-sm rounded-md whitespace-pre-line">
                    {getDeleteFileValueDisplay(log)}
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t">
          <div className="flex items-center justify-end">
            <button className="btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogDetailsOverlay;
